
import React, { useState, useEffect } from 'react';
import { Save, Key, Cpu, Zap, CheckCircle, Shield, Globe, AlertTriangle, Trash2, X, Table, ExternalLink } from 'lucide-react';
import { AppData } from '../types';

interface SettingsProps {
  data: AppData;
}

export const Settings: React.FC<SettingsProps> = ({ data }) => {
  const { t, aiConfig, updateAIConfig } = data;
  const [activeProvider, setActiveProvider] = useState('groq');
  const [status, setStatus] = useState('');

  // Groq State
  const [groqKey, setGroqKey] = useState('');
  const [groqModel, setGroqModel] = useState('');
  
  // Gemini State
  const [geminiKey, setGeminiKey] = useState('');
  const [geminiModel, setGeminiModel] = useState('');

  // OpenAI State
  const [openaiKey, setOpenaiKey] = useState('');
  const [openaiModel, setOpenaiModel] = useState('');

  // OpenRouter State
  const [openrouterKey, setOpenrouterKey] = useState('');
  const [openrouterModel, setOpenrouterModel] = useState('');
  
  // Google Sheets State
  const [sheetsUrl, setSheetsUrl] = useState('');

  // Reset State
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');

  // Defaults
  const DEFAULTS = {
    groq: { key: "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", model: "llama-3.3-70b-versatile" },
    gemini: { key: "", model: "gemini-3-flash-preview" },
    openai: { key: "", model: "gpt-4o" },
    openrouter: { key: "", model: "anthropic/claude-3.5-sonnet" }
  };

  useEffect(() => {
    // Load settings from localStorage or use defaults
    const savedProvider = localStorage.getItem('active_provider');

    setGroqKey(localStorage.getItem('groq_api_key') || DEFAULTS.groq.key);
    setGroqModel(localStorage.getItem('groq_model') || DEFAULTS.groq.model);

    setGeminiKey(localStorage.getItem('gemini_api_key') || DEFAULTS.gemini.key);
    setGeminiModel(localStorage.getItem('gemini_model') || DEFAULTS.gemini.model);

    setOpenaiKey(localStorage.getItem('openai_api_key') || DEFAULTS.openai.key);
    setOpenaiModel(localStorage.getItem('openai_model') || DEFAULTS.openai.model);

    setOpenrouterKey(localStorage.getItem('openrouter_api_key') || DEFAULTS.openrouter.key);
    setOpenrouterModel(localStorage.getItem('openrouter_model') || DEFAULTS.openrouter.model);
    
    // Load Sheet URL from App Data (which loads from LocalStorage in App.tsx)
    setSheetsUrl(aiConfig.googleSheetsUrl || '');
    
    if (savedProvider) {
        setActiveProvider(savedProvider);
    } else {
        setActiveProvider('groq');
        localStorage.setItem('active_provider', 'groq');
    }
  }, [aiConfig]);

  const handleSave = () => {
    localStorage.setItem('groq_api_key', groqKey);
    localStorage.setItem('groq_model', groqModel);
    
    localStorage.setItem('gemini_api_key', geminiKey);
    localStorage.setItem('gemini_model', geminiModel);

    localStorage.setItem('openai_api_key', openaiKey);
    localStorage.setItem('openai_model', openaiModel);

    localStorage.setItem('openrouter_api_key', openrouterKey);
    localStorage.setItem('openrouter_model', openrouterModel);

    localStorage.setItem('active_provider', activeProvider);
    
    // Update the main context with the new Sheets URL
    const newConfig = { ...aiConfig, googleSheetsUrl: sheetsUrl };
    updateAIConfig(newConfig);

    setStatus('All Settings Saved!');
    setTimeout(() => setStatus(''), 3000);
  };

  const handleReset = () => {
    if (confirm("Reset current provider to defaults?")) {
        if (activeProvider === 'groq') {
            setGroqKey(DEFAULTS.groq.key);
            setGroqModel(DEFAULTS.groq.model);
        } else if (activeProvider === 'gemini') {
            setGeminiKey(DEFAULTS.gemini.key);
            setGeminiModel(DEFAULTS.gemini.model);
        } else if (activeProvider === 'openai') {
            setOpenaiKey(DEFAULTS.openai.key);
            setOpenaiModel(DEFAULTS.openai.model);
        } else if (activeProvider === 'openrouter') {
            setOpenrouterKey(DEFAULTS.openrouter.key);
            setOpenrouterModel(DEFAULTS.openrouter.model);
        }
        setStatus('Reset to defaults.');
        setTimeout(() => setStatus(''), 2000);
    }
  };

  const handleFactoryReset = () => {
    if (resetPassword === 'alibaba') {
      data.resetAllData();
      setShowResetModal(false);
      // No reload needed, state updates will clear the UI
      setStatus('All business data erased successfully.');
      setTimeout(() => setStatus(''), 4000);
    } else {
      setResetError("Incorrect password.");
    }
  };

  const ProviderButton = ({ id, name, icon: Icon, active }: any) => (
    <button
      onClick={() => setActiveProvider(id)}
      className={`w-full flex items-center gap-3 p-4 rounded-xl transition-all duration-200 border text-left ${
        active 
          ? 'bg-white border-indigo-500 shadow-md ring-1 ring-indigo-100' 
          : 'bg-transparent border-transparent hover:bg-gray-100 text-gray-500'
      }`}
    >
      <div className={`p-2 rounded-lg ${active ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className={`font-bold text-sm ${active ? 'text-gray-900' : 'text-gray-500'}`}>{name}</p>
        {active && <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider">Active</span>}
      </div>
    </button>
  );

  const ConfigSection = ({ title, icon: Icon, apiKey, setApiKey, model, setModel, placeholder, models, colorClass }: any) => (
    <>
        <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClass.bg} ${colorClass.text}`}>
                    <Icon size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{title} {t.nav_settings}</h3>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1">
                <CheckCircle size={12} /> ACTIVE
            </span>
        </div>

        <div className="space-y-6">
            <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">API Key</label>
                <div className="relative">
                    <input 
                        type="password" 
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        className={`w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-xl p-4 pl-10 focus:ring-2 focus:ring-${colorClass.ring} focus:border-${colorClass.ring} outline-none transition font-mono`}
                        placeholder="sk-..."
                    />
                    <Key size={16} className="absolute left-3.5 top-4.5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-400 mt-2">Key stored locally in browser.</p>
            </div>

            <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Model</label>
                {models ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {models.map((m: any) => (
                             <div 
                                key={m.id}
                                onClick={() => setModel(m.id)}
                                className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${
                                    model === m.id 
                                    ? `border-${colorClass.ring} ${colorClass.bg}` 
                                    : 'border-gray-100 hover:border-gray-200'
                                }`}
                            >
                                <p className="font-bold text-gray-800 text-sm">{m.id}</p>
                                {m.tag && <span className="text-[10px] font-bold text-gray-400 bg-white px-2 py-0.5 rounded border border-gray-200 mt-2 inline-block">{m.tag}</span>}
                            </div>
                        ))}
                    </div>
                ) : (
                    <input 
                        type="text" 
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 p-4 rounded-xl text-sm font-medium"
                        placeholder={placeholder}
                    />
                )}
            </div>
        </div>
    </>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold text-gray-900">AI Control Center</h2>
        <p className="text-gray-500 mt-2">Configure intelligence providers for your dashboard.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Sidebar */}
        <div className="md:col-span-4 space-y-2">
            <ProviderButton id="groq" name="Groq" icon={Cpu} active={activeProvider === 'groq'} />
            <ProviderButton id="sheets" name="Google Sheets Sync" icon={Table} active={activeProvider === 'sheets'} />
            <ProviderButton id="gemini" name="Google Gemini" icon={Zap} active={activeProvider === 'gemini'} />
            <ProviderButton id="openai" name="OpenAI" icon={Shield} active={activeProvider === 'openai'} />
            <ProviderButton id="openrouter" name="OpenRouter" icon={Globe} active={activeProvider === 'openrouter'} />
        </div>

        {/* Configuration Panel */}
        <div className="md:col-span-8 space-y-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                
                {activeProvider === 'sheets' && (
                    <>
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-green-50 text-green-600">
                                    <Table size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">Google Sheets Integration</h3>
                            </div>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 text-sm text-blue-800">
                            <p className="font-bold flex items-center gap-2 mb-2"><Globe size={16}/> How to set up:</p>
                            <ol className="list-decimal ml-4 space-y-1">
                                <li>Create a new <b>Google Apps Script</b> project.</li>
                                <li>Paste the integration code (provided by developer).</li>
                                <li>Deploy as <b>Web App</b> (Access: Anyone).</li>
                                <li>Paste the <b>Deployment URL</b> below.</li>
                            </ol>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Web App Deployment URL</label>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    value={sheetsUrl}
                                    onChange={(e) => setSheetsUrl(e.target.value)}
                                    className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-xl p-4 pl-10 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition font-mono"
                                    placeholder="https://script.google.com/macros/s/..."
                                />
                                <ExternalLink size={16} className="absolute left-3.5 top-4.5 text-gray-400" />
                            </div>
                            <p className="text-xs text-gray-400 mt-2">Website Chat will push leads here. CRM will read leads from here.</p>
                        </div>
                    </>
                )}

                {activeProvider === 'groq' && (
                    <ConfigSection 
                        title="Groq" 
                        icon={Cpu} 
                        apiKey={groqKey} 
                        setApiKey={setGroqKey} 
                        model={groqModel} 
                        setModel={setGroqModel}
                        colorClass={{ bg: 'bg-red-50', text: 'text-red-600', ring: 'red-500' }}
                        models={[
                            { id: 'llama-3.3-70b-versatile', tag: 'BALANCED' },
                            { id: 'llama-3.1-8b-instant', tag: 'FASTEST' }
                        ]}
                    />
                )}

                {activeProvider === 'gemini' && (
                    <ConfigSection 
                        title="Google Gemini" 
                        icon={Zap} 
                        apiKey={geminiKey} 
                        setApiKey={setGeminiKey} 
                        model={geminiModel} 
                        setModel={setGeminiModel}
                        colorClass={{ bg: 'bg-blue-50', text: 'text-blue-600', ring: 'blue-500' }}
                        models={[
                            { id: 'gemini-3-flash-preview', tag: 'FAST & SMART' },
                            { id: 'gemini-3-pro-preview', tag: 'REASONING' }
                        ]}
                    />
                )}

                {activeProvider === 'openai' && (
                    <ConfigSection 
                        title="OpenAI" 
                        icon={Shield} 
                        apiKey={openaiKey} 
                        setApiKey={setOpenaiKey} 
                        model={openaiModel} 
                        setModel={setOpenaiModel}
                        colorClass={{ bg: 'bg-green-50', text: 'text-green-600', ring: 'green-500' }}
                        models={[
                            { id: 'gpt-4o', tag: 'BEST QUALITY' },
                            { id: 'gpt-4o-mini', tag: 'COST EFFICIENT' }
                        ]}
                    />
                )}

                {activeProvider === 'openrouter' && (
                    <ConfigSection 
                        title="OpenRouter" 
                        icon={Globe} 
                        apiKey={openrouterKey} 
                        setApiKey={setOpenrouterKey} 
                        model={openrouterModel} 
                        setModel={setOpenrouterModel}
                        placeholder="e.g. anthropic/claude-3.5-sonnet"
                        colorClass={{ bg: 'bg-purple-50', text: 'text-purple-600', ring: 'purple-500' }}
                    />
                )}

                {/* Actions */}
                <div className="pt-6 border-t border-gray-100 flex items-center gap-4 mt-8">
                    <button 
                        onClick={handleSave}
                        className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition shadow-lg shadow-indigo-200 flex items-center gap-2"
                    >
                        <Save size={18} /> {t.evt_save_btn}
                    </button>
                    <button 
                        onClick={handleReset}
                        className="px-6 py-3 text-gray-500 hover:bg-gray-50 font-bold rounded-xl transition"
                    >
                        Reset Defaults
                    </button>
                    {status && <span className="text-green-600 font-medium text-sm animate-fade-in">{status}</span>}
                </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-red-50 rounded-2xl border border-red-100 p-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-100 text-red-600 rounded-lg">
                        <AlertTriangle size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-red-900">Danger Zone</h3>
                </div>
                <p className="text-red-700 mb-6 text-sm leading-relaxed">
                    This action will permanently erase all events, payments, and expenses. 
                    Configuration settings (API keys) will be preserved.
                    This cannot be undone.
                </p>
                <button 
                    onClick={() => setShowResetModal(true)}
                    className="w-full bg-white border border-red-200 text-red-600 hover:bg-red-600 hover:text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2"
                >
                    <Trash2 size={18} /> Erase All Data
                </button>
            </div>
        </div>
      </div>

      {/* Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 transform transition-all scale-100">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-red-600 flex items-center gap-2">
                        <AlertTriangle size={20} /> Factory Reset
                    </h3>
                    <button onClick={() => setShowResetModal(false)} className="text-gray-400 hover:text-gray-600">
                        <X size={20} />
                    </button>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">Enter admin password to confirm deletion of all data.</p>
                
                <input 
                    type="password"
                    placeholder={t.password}
                    className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-red-500 outline-none mb-1"
                    value={resetPassword}
                    onChange={(e) => {
                        setResetPassword(e.target.value);
                        setResetError('');
                    }}
                    autoFocus
                />
                <p className="text-xs text-gray-400 mb-4 italic">Hint: 40thiefs</p>
                
                {resetError && <p className="text-sm text-red-600 font-bold mb-4">{resetError}</p>}

                <div className="flex gap-3">
                    <button 
                        onClick={() => setShowResetModal(false)}
                        className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium"
                    >
                        {t.evt_cancel_btn}
                    </button>
                    <button 
                        onClick={handleFactoryReset}
                        className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md shadow-red-200"
                    >
                        {t.confirm}
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};
